import React, { useState, useEffect, useCallback } from 'react';
import { useLocalization } from '../../hooks/useLocalization';

interface CachedPrice {
    date: string;
    nightCount: number;
    adults: number;
    children: number;
    minPrice: number;
    currency: string;
    roomType: string;
    boardType: string;
    offerId: string;
    departureCity: string;
    includesFlight: boolean;
    cachedAt: string;
}

interface CacheStats {
    totalEntries: number;
    minPrice: number;
    maxPrice: number;
    avgPrice: number;
    currencies: string[];
    roomTypes: string[];
    boardTypes: string[];
    lastUpdated: string | null;
}

interface SearchHistoryEntry {
    searchId: string;
    searchParams: {
        checkIn?: string;
        night?: number;
        departureId?: string;
        departureName?: string;
        arrivalId?: string;
        arrivalName?: string;
        adults?: number;
        children?: number;
    };
    cachedAt: string;
    totalHotels: number;
    totalOffers: number;
    hotelOffers: number;
    lowestPrice: number;
    currency: string;
}

interface HotelOnlyOffer {
    checkIn: string;
    nights: number;
    roomType: string;
    board: string;
    price: number;
    currency: string;
    offerId: string;
}

interface PackageOffer {
    checkIn: string;
    nights: number;
    roomType: string;
    board: string;
    price: number;
    currency: string;
    departure: string;
    offerId: string;
}

interface FlightEntry {
    checkIn: string;
    airline: string;
    flightNo: string;
    departure: string;
    arrival: string;
    departureTime: string;
    arrivalTime: string;
    price: number;
    currency: string;
    offerId: string;
}

interface HotelCacheManagerProps {
    hotelId: string;
    hotelName: string;
}

// Local translations for admin-only cache manager module
const labels: Record<string, Record<string, string>> = {
    en: {
        loading: 'Loading cache data...',
        cache_entries: 'Cache Entries',
        min_price: 'Min Price',
        max_price: 'Max Price',
        avg_price: 'Avg Price',
        last_updated: 'Last Updated',
        cached_prices: 'Cached Prices',
        fetch_prices: 'Fetch Prices',
        search_history: 'Search History',
        date: 'Date',
        nights: 'Nights',
        guests: 'Guests',
        room_type: 'Room Type',
        board: 'Board',
        departure_city: 'Departure',
        flight: 'Flight',
        price: 'Price',
        cached_at: 'Cached At',
        filter_date: 'Date',
        filter_room_type: 'Room type',
        filter_room_placeholder: 'Filter room type...',
        flights_only: 'Flights only',
        clear_filter: 'Clear filters',
        no_cached_prices: 'No cached prices found',
        fetch_from_tourvisio: 'Fetch Prices from Tourvisio',
        start_date: 'Start Date',
        end_date: 'End Date',
        night_count: 'Nights',
        night_label: 'nights',
        fetch_btn: 'Fetch Prices',
        fetching: 'Fetching...',
        fetching_info: 'Fetching prices from Tourvisio... This may take a while depending on the date range.',
        hotel_only: 'Hotel Only',
        packages: 'Packages (with flight)',
        flights_tab: 'Flights',
        from_price: 'from',
        checkin: 'Check-in',
        package_price: 'Package Price',
        airline: 'Airline',
        flight_no: 'Flight No',
        from_airport: 'From',
        to_airport: 'To',
        time: 'Time',
        no_hotel_only: 'No hotel-only prices found',
        no_packages: 'No package prices found',
        no_flights: 'No flights found (flight data is added when available in offers)',
        no_search_history: 'No search history found for this hotel.',
        route: 'Route',
        hotels_total: 'Hotels (total)',
        offers: 'Offers',
        lowest_price: 'Lowest Price',
        cached: 'Cached',
        yes: 'Yes',
    },
    sv: {
        loading: 'Laddar cache-data...',
        cache_entries: 'Cache-poster',
        min_price: 'Min Pris',
        max_price: 'Max Pris',
        avg_price: 'Snitt Pris',
        last_updated: 'Senast Uppdaterad',
        cached_prices: 'Cachade Priser',
        fetch_prices: 'Hämta Priser',
        search_history: 'Sökhistorik',
        date: 'Datum',
        nights: 'Nätter',
        guests: 'Gäster',
        room_type: 'Rumstyp',
        board: 'Pension',
        departure_city: 'Avresa',
        flight: 'Flyg',
        price: 'Pris',
        cached_at: 'Cachad',
        filter_date: 'Datum',
        filter_room_type: 'Rumstyp',
        filter_room_placeholder: 'Filtrera rumstyp...',
        flights_only: 'Endast med flyg',
        clear_filter: 'Rensa filter',
        no_cached_prices: 'Inga cachade priser hittades',
        fetch_from_tourvisio: 'Hämta Priser från Tourvisio',
        start_date: 'Startdatum',
        end_date: 'Slutdatum',
        night_count: 'Nätter',
        night_label: 'nätter',
        fetch_btn: 'Hämta Priser',
        fetching: 'Hämtar...',
        fetching_info: 'Hämtar priser från Tourvisio... Detta kan ta en stund beroende på datumintervallet.',
        hotel_only: 'Endast Hotell',
        packages: 'Paket (med flyg)',
        flights_tab: 'Flyg',
        from_price: 'från',
        checkin: 'Incheckning',
        package_price: 'Paketpris',
        airline: 'Flygbolag',
        flight_no: 'Flygnr',
        from_airport: 'Från',
        to_airport: 'Till',
        time: 'Tid',
        no_hotel_only: 'Inga hotel-only priser hittades',
        no_packages: 'Inga paketpriser hittades',
        no_flights: 'Inga flyg hittades (flygdata läggs till när den finns tillgänglig i erbjudanden)',
        no_search_history: 'Ingen sökhistorik hittades för detta hotell.',
        route: 'Rutt',
        hotels_total: 'Hotell (totalt)',
        offers: 'Erbjudanden',
        lowest_price: 'Lägsta Pris',
        cached: 'Cachad',
        yes: 'Ja',
    }
};

const HotelCacheManager: React.FC<HotelCacheManagerProps> = ({ hotelId, hotelName }) => {
    const { language } = useLocalization();
    const l = labels[language] || labels.en;

    const [activeTab, setActiveTab] = useState<'prices' | 'history' | 'fetch'>('prices');
    const [cachedPrices, setCachedPrices] = useState<CachedPrice[]>([]);
    const [searchHistory, setSearchHistory] = useState<SearchHistoryEntry[]>([]);
    const [stats, setStats] = useState<CacheStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const [dateFilter, setDateFilter] = useState('');
    const [roomTypeFilter, setRoomTypeFilter] = useState('');
    const [showFlightsOnly, setShowFlightsOnly] = useState(false);

    const [fetchStartDate, setFetchStartDate] = useState('');
    const [fetchEndDate, setFetchEndDate] = useState('');
    const [fetchNights, setFetchNights] = useState(7);
    const [fetching, setFetching] = useState(false);
    const [fetchResultTab, setFetchResultTab] = useState<'hotelOnly' | 'packages' | 'flights'>('hotelOnly');
    const [hotelOnlyResults, setHotelOnlyResults] = useState<HotelOnlyOffer[]>([]);
    const [packageResults, setPackageResults] = useState<PackageOffer[]>([]);
    const [flightResults, setFlightResults] = useState<FlightEntry[]>([]);
    const [fetchSummary, setFetchSummary] = useState<any>(null);

    useEffect(() => {
        const now = new Date();
        const start = new Date(now);
        start.setDate(start.getDate() + 14);
        const end = new Date(start);
        end.setMonth(end.getMonth() + 2);
        setFetchStartDate(start.toISOString().split('T')[0]);
        setFetchEndDate(end.toISOString().split('T')[0]);
    }, []);

    const fetchCacheData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(`/api/hotels/${hotelId}/cached-data`);
            const data = await response.json();
            if (data.success) {
                setCachedPrices(data.cachedPrices || []);
                setStats(data.stats || null);
            }
        } catch (error) {
            console.error('Failed to fetch cache:', error);
        } finally {
            setLoading(false);
        }
    }, [hotelId]);

    const fetchSearchHistory = useCallback(async () => {
        try {
            const response = await fetch(`/api/hotels/${hotelId}/search-history`);
            const data = await response.json();
            if (data.success) {
                setSearchHistory(data.searchHistory || []);
            }
        } catch (error) {
            console.error('Failed to fetch search history:', error);
        }
    }, [hotelId]);

    useEffect(() => {
        fetchCacheData();
        fetchSearchHistory();
    }, [fetchCacheData, fetchSearchHistory]);

    const handleFetchPrices = async () => {
        if (!fetchStartDate || !fetchEndDate || fetching) return;
        setFetching(true);
        setMessage(null);
        setHotelOnlyResults([]);
        setPackageResults([]);
        setFlightResults([]);
        setFetchSummary(null);

        try {
            const response = await fetch(`/api/admin/rooms/hotel/${hotelId}/fetch-prices`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    startDate: fetchStartDate,
                    endDate: fetchEndDate,
                    nights: fetchNights
                })
            });
            const data = await response.json();

            if (data.success) {
                setHotelOnlyResults(data.hotelOnly || []);
                setPackageResults(data.packages || []);
                setFlightResults(data.flights || []);
                setFetchSummary(data.summary);
                setMessage({
                    type: 'success',
                    text: `${data.summary?.hotelOnlyCount || 0} hotel-only, ${data.summary?.packagesCount || 0} ${language === 'sv' ? 'paket' : 'packages'}, ${data.summary?.flightsCount || 0} ${language === 'sv' ? 'flyg' : 'flights'}`
                });
                fetchCacheData();
                fetchSearchHistory();
            } else {
                setMessage({ type: 'error', text: data.message || 'Failed to fetch prices' });
            }
        } catch (error: any) {
            setMessage({ type: 'error', text: error.message || 'Network error' });
        } finally {
            setFetching(false);
        }
    };

    const filteredPrices = cachedPrices.filter(price => {
        if (dateFilter && !price.date?.includes(dateFilter)) return false;
        if (roomTypeFilter && !price.roomType?.toLowerCase().includes(roomTypeFilter.toLowerCase())) return false;
        if (showFlightsOnly && !price.includesFlight) return false;
        return true;
    });

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString(language === 'sv' ? 'sv-SE' : 'en-US');
    };

    const formatDateTime = (dateStr: string) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString(language === 'sv' ? 'sv-SE' : 'en-US');
    };

    if (loading && cachedPrices.length === 0) {
        return <div className="p-8 text-center text-gray-400">{l.loading}</div>;
    }

    const totalFetchResults = hotelOnlyResults.length + packageResults.length + flightResults.length;

    return (
        <div className="space-y-6">
            {message && (
                <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}`}>
                    {message.text}
                </div>
            )}

            {/* Stats Header */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-slate-700/50 p-4 rounded-xl border border-slate-600">
                        <div className="text-gray-400 text-xs uppercase font-bold">{l.cache_entries}</div>
                        <div className="text-2xl font-bold text-amber-400">{stats.totalEntries}</div>
                    </div>
                    <div className="bg-slate-700/50 p-4 rounded-xl border border-slate-600">
                        <div className="text-gray-400 text-xs uppercase font-bold">{l.min_price}</div>
                        <div className="text-2xl font-bold text-teal-400">{stats.minPrice.toLocaleString()} {stats.currencies[0] || 'EUR'}</div>
                    </div>
                    <div className="bg-slate-700/50 p-4 rounded-xl border border-slate-600">
                        <div className="text-gray-400 text-xs uppercase font-bold">{l.max_price}</div>
                        <div className="text-2xl font-bold text-rose-400">{stats.maxPrice.toLocaleString()} {stats.currencies[0] || 'EUR'}</div>
                    </div>
                    <div className="bg-slate-700/50 p-4 rounded-xl border border-slate-600">
                        <div className="text-gray-400 text-xs uppercase font-bold">{l.avg_price}</div>
                        <div className="text-2xl font-bold text-blue-400">{stats.avgPrice.toLocaleString()} {stats.currencies[0] || 'EUR'}</div>
                    </div>
                    <div className="bg-slate-700/50 p-4 rounded-xl border border-slate-600">
                        <div className="text-gray-400 text-xs uppercase font-bold">{l.last_updated}</div>
                        <div className="text-sm font-medium text-gray-300">{stats.lastUpdated ? formatDateTime(stats.lastUpdated) : '-'}</div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-2 border-b border-slate-700 pb-3">
                <button
                    onClick={() => setActiveTab('prices')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'prices'
                        ? 'bg-teal-600 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-slate-700'
                        }`}
                >
                    💰 {l.cached_prices} ({cachedPrices.length})
                </button>
                <button
                    onClick={() => setActiveTab('fetch')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'fetch'
                        ? 'bg-amber-600 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-slate-700'
                        }`}
                >
                    🔍 {l.fetch_prices} {totalFetchResults > 0 ? `(${totalFetchResults})` : ''}
                </button>
                <button
                    onClick={() => setActiveTab('history')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'history'
                        ? 'bg-teal-600 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-slate-700'
                        }`}
                >
                    📜 {l.search_history} ({searchHistory.length})
                </button>
            </div>

            {/* Prices Tab */}
            {activeTab === 'prices' && (
                <>
                    <div className="flex flex-wrap gap-4 items-end bg-slate-800 p-4 rounded-xl border border-slate-700">
                        <div>
                            <label className="block text-xs text-gray-400 mb-1">{l.filter_date}</label>
                            <input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}
                                className="bg-slate-700 border border-slate-600 rounded px-3 py-1.5 text-sm text-white focus:ring-1 focus:ring-teal-500 outline-none" />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-400 mb-1">{l.filter_room_type}</label>
                            <input type="text" placeholder={l.filter_room_placeholder} value={roomTypeFilter} onChange={(e) => setRoomTypeFilter(e.target.value)}
                                className="bg-slate-700 border border-slate-600 rounded px-3 py-1.5 text-sm text-white focus:ring-1 focus:ring-teal-500 outline-none" />
                        </div>
                        <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                            <input type="checkbox" checked={showFlightsOnly} onChange={(e) => setShowFlightsOnly(e.target.checked)}
                                className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-teal-500 focus:ring-teal-500" />
                            {l.flights_only}
                        </label>
                        <button onClick={() => { setDateFilter(''); setRoomTypeFilter(''); setShowFlightsOnly(false); }}
                            className="px-3 py-1.5 text-sm text-gray-400 hover:text-white bg-slate-700/50 rounded-lg transition-colors">
                            {l.clear_filter}
                        </button>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-gray-400 text-xs uppercase border-b border-slate-700">
                                    <th className="p-3">{l.date}</th>
                                    <th className="p-3">{l.nights}</th>
                                    <th className="p-3">{l.guests}</th>
                                    <th className="p-3">{l.room_type}</th>
                                    <th className="p-3">{l.board}</th>
                                    <th className="p-3">{l.departure_city}</th>
                                    <th className="p-3">{l.flight}</th>
                                    <th className="p-3 text-right">{l.price}</th>
                                    <th className="p-3">{l.cached_at}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700/50">
                                {filteredPrices.length === 0 ? (
                                    <tr><td colSpan={9} className="p-8 text-center text-gray-500">{l.no_cached_prices}</td></tr>
                                ) : (
                                    filteredPrices.map((price, idx) => (
                                        <tr key={idx} className="hover:bg-slate-800/50 transition-colors">
                                            <td className="p-3">{formatDate(price.date)}</td>
                                            <td className="p-3">{price.nightCount}</td>
                                            <td className="p-3">{price.adults}A {price.children > 0 ? `+ ${price.children}C` : ''}</td>
                                            <td className="p-3 max-w-[150px] truncate" title={price.roomType}>{price.roomType || '-'}</td>
                                            <td className="p-3">{price.boardType || '-'}</td>
                                            <td className="p-3">{price.departureCity || '-'}</td>
                                            <td className="p-3">
                                                {price.includesFlight ? <span className="text-teal-400">✈️ {l.yes}</span> : <span className="text-gray-500">-</span>}
                                            </td>
                                            <td className="p-3 text-right font-bold text-teal-400">{price.minPrice?.toLocaleString()} {price.currency}</td>
                                            <td className="p-3 text-gray-400 text-xs">{formatDateTime(price.cachedAt)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* Fetch Prices Tab */}
            {activeTab === 'fetch' && (
                <div className="space-y-6">
                    <div className="bg-slate-800 p-5 rounded-xl border border-slate-700">
                        <h3 className="text-white font-bold text-sm mb-4 flex items-center gap-2">
                            🔍 {l.fetch_from_tourvisio}
                        </h3>
                        <div className="flex flex-wrap gap-4 items-end">
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">{l.start_date}</label>
                                <input type="date" value={fetchStartDate} onChange={(e) => setFetchStartDate(e.target.value)}
                                    className="bg-slate-700 border border-slate-600 rounded px-3 py-2 text-sm text-white focus:ring-1 focus:ring-amber-500 outline-none" />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">{l.end_date}</label>
                                <input type="date" value={fetchEndDate} onChange={(e) => setFetchEndDate(e.target.value)}
                                    className="bg-slate-700 border border-slate-600 rounded px-3 py-2 text-sm text-white focus:ring-1 focus:ring-amber-500 outline-none" />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">{l.night_count}</label>
                                <select value={fetchNights} onChange={(e) => setFetchNights(parseInt(e.target.value))}
                                    className="bg-slate-700 border border-slate-600 rounded px-3 py-2 text-sm text-white focus:ring-1 focus:ring-amber-500 outline-none">
                                    {[3, 5, 7, 10, 14].map(n => (
                                        <option key={n} value={n}>{n} {l.night_label}</option>
                                    ))}
                                </select>
                            </div>
                            <button onClick={handleFetchPrices} disabled={fetching || !fetchStartDate || !fetchEndDate}
                                className="px-6 py-2 bg-amber-600 text-white rounded-lg font-bold text-sm hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all">
                                {fetching ? (<>⏳ {l.fetching}</>) : (<>🔍 {l.fetch_btn}</>)}
                            </button>
                        </div>
                        {fetching && (
                            <div className="mt-4 p-3 bg-amber-900/20 border border-amber-800 rounded-lg text-amber-400 text-sm">
                                ⏳ {l.fetching_info}
                            </div>
                        )}
                    </div>

                    {totalFetchResults > 0 && (
                        <div className="space-y-4">
                            {fetchSummary && (
                                <div className="grid grid-cols-3 gap-4">
                                    <div onClick={() => setFetchResultTab('hotelOnly')}
                                        className={`p-4 rounded-xl border cursor-pointer transition-all ${fetchResultTab === 'hotelOnly' ? 'bg-blue-600/20 border-blue-500' : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'}`}>
                                        <div className="text-xs text-gray-400 uppercase font-bold mb-1">🏨 {l.hotel_only}</div>
                                        <div className="text-2xl font-bold text-blue-400">{fetchSummary.hotelOnlyCount}</div>
                                        {fetchSummary.hotelOnlyMinPrice > 0 && (
                                            <div className="text-xs text-gray-400 mt-1">{l.from_price} {fetchSummary.hotelOnlyMinPrice.toLocaleString()} EUR</div>
                                        )}
                                    </div>
                                    <div onClick={() => setFetchResultTab('packages')}
                                        className={`p-4 rounded-xl border cursor-pointer transition-all ${fetchResultTab === 'packages' ? 'bg-emerald-600/20 border-emerald-500' : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'}`}>
                                        <div className="text-xs text-gray-400 uppercase font-bold mb-1">📦 {l.packages}</div>
                                        <div className="text-2xl font-bold text-emerald-400">{fetchSummary.packagesCount}</div>
                                        {fetchSummary.packagesMinPrice > 0 && (
                                            <div className="text-xs text-gray-400 mt-1">{l.from_price} {fetchSummary.packagesMinPrice.toLocaleString()} SEK</div>
                                        )}
                                    </div>
                                    <div onClick={() => setFetchResultTab('flights')}
                                        className={`p-4 rounded-xl border cursor-pointer transition-all ${fetchResultTab === 'flights' ? 'bg-purple-600/20 border-purple-500' : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'}`}>
                                        <div className="text-xs text-gray-400 uppercase font-bold mb-1">✈️ {l.flights_tab}</div>
                                        <div className="text-2xl font-bold text-purple-400">{fetchSummary.flightsCount}</div>
                                    </div>
                                </div>
                            )}

                            {fetchResultTab === 'hotelOnly' && (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-gray-400 text-xs uppercase border-b border-slate-700">
                                                <th className="p-3">{l.checkin}</th>
                                                <th className="p-3">{l.nights}</th>
                                                <th className="p-3">{l.room_type}</th>
                                                <th className="p-3">{l.board}</th>
                                                <th className="p-3 text-right">{l.price}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-700/50">
                                            {hotelOnlyResults.length === 0 ? (
                                                <tr><td colSpan={5} className="p-8 text-center text-gray-500">{l.no_hotel_only}</td></tr>
                                            ) : (
                                                hotelOnlyResults.map((o, idx) => (
                                                    <tr key={idx} className="hover:bg-slate-800/50">
                                                        <td className="p-3">{formatDate(o.checkIn)}</td>
                                                        <td className="p-3">{o.nights}</td>
                                                        <td className="p-3 max-w-[180px] truncate" title={o.roomType}>{o.roomType || '-'}</td>
                                                        <td className="p-3">{o.board || '-'}</td>
                                                        <td className="p-3 text-right font-bold text-blue-400">{o.price?.toLocaleString()} {o.currency}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {fetchResultTab === 'packages' && (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-gray-400 text-xs uppercase border-b border-slate-700">
                                                <th className="p-3">{l.checkin}</th>
                                                <th className="p-3">{l.nights}</th>
                                                <th className="p-3">{l.room_type}</th>
                                                <th className="p-3">{l.board}</th>
                                                <th className="p-3">{l.departure_city}</th>
                                                <th className="p-3 text-right">{l.package_price}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-700/50">
                                            {packageResults.length === 0 ? (
                                                <tr><td colSpan={6} className="p-8 text-center text-gray-500">{l.no_packages}</td></tr>
                                            ) : (
                                                packageResults.map((o, idx) => (
                                                    <tr key={idx} className="hover:bg-slate-800/50">
                                                        <td className="p-3">{formatDate(o.checkIn)}</td>
                                                        <td className="p-3">{o.nights}</td>
                                                        <td className="p-3 max-w-[180px] truncate" title={o.roomType}>{o.roomType || '-'}</td>
                                                        <td className="p-3">{o.board || '-'}</td>
                                                        <td className="p-3">{o.departure}</td>
                                                        <td className="p-3 text-right font-bold text-emerald-400">{o.price?.toLocaleString()} {o.currency}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {fetchResultTab === 'flights' && (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-gray-400 text-xs uppercase border-b border-slate-700">
                                                <th className="p-3">{l.checkin}</th>
                                                <th className="p-3">{l.airline}</th>
                                                <th className="p-3">{l.flight_no}</th>
                                                <th className="p-3">{l.from_airport}</th>
                                                <th className="p-3">{l.to_airport}</th>
                                                <th className="p-3">{l.time}</th>
                                                <th className="p-3 text-right">{l.package_price}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-700/50">
                                            {flightResults.length === 0 ? (
                                                <tr><td colSpan={7} className="p-8 text-center text-gray-500">{l.no_flights}</td></tr>
                                            ) : (
                                                flightResults.map((f, idx) => (
                                                    <tr key={idx} className="hover:bg-slate-800/50">
                                                        <td className="p-3">{formatDate(f.checkIn)}</td>
                                                        <td className="p-3">{f.airline || '-'}</td>
                                                        <td className="p-3">{f.flightNo || '-'}</td>
                                                        <td className="p-3 font-mono">{f.departure || '-'}</td>
                                                        <td className="p-3 font-mono">{f.arrival || '-'}</td>
                                                        <td className="p-3">{f.departureTime || '-'}</td>
                                                        <td className="p-3 text-right font-bold text-purple-400">{f.price?.toLocaleString()} {f.currency}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
                <div className="space-y-3">
                    {searchHistory.length === 0 ? (
                        <div className="py-12 text-center text-gray-500 bg-slate-800/50 rounded-xl border border-dashed border-slate-700">
                            {l.no_search_history}
                        </div>
                    ) : (
                        searchHistory.map((entry, idx) => (
                            <div key={idx} className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 hover:border-slate-600 transition-colors">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-6">
                                        <div className="flex flex-col">
                                            <span className="text-xs text-gray-500 uppercase font-bold">{l.route}</span>
                                            <span className="text-sm font-medium">
                                                {entry.searchParams.departureName || entry.searchParams.departureId || '?'} → {entry.searchParams.arrivalName || entry.searchParams.arrivalId || '?'}
                                            </span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-xs text-gray-500 uppercase font-bold">{l.checkin}</span>
                                            <span className="text-sm">{entry.searchParams.checkIn ? formatDate(entry.searchParams.checkIn) : '-'}</span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-xs text-gray-500 uppercase font-bold">{l.nights}</span>
                                            <span className="text-sm">{entry.searchParams.night || '-'}</span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-xs text-gray-500 uppercase font-bold">{l.hotels_total}</span>
                                            <span className="text-sm">{entry.totalHotels}</span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-xs text-gray-500 uppercase font-bold">{l.offers}</span>
                                            <span className="text-sm">{entry.hotelOffers}</span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-xs text-gray-500 uppercase font-bold">{l.lowest_price}</span>
                                            <span className="text-sm font-bold text-teal-400">{entry.lowestPrice?.toLocaleString()} {entry.currency}</span>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] text-gray-500 uppercase font-bold">{l.cached}</div>
                                        <div className="text-xs text-gray-400">{formatDateTime(entry.cachedAt)}</div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default HotelCacheManager;
